import React from 'react';
import { Navigate } from 'react-router-dom';
import { PageShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';

const Account: React.FC = () => {
  const { userName, userEmail, logout } = useAuth();
  const { user, userMode } = useApp();

  // A demo session has no real account, no Sign Out, nothing to manage here — redirect
  // away rather than render a Sign Out button (which would call MSAL logoutRedirect
  // against no real account) or fake-looking identity fields. Conditionally rendered via
  // early return, not CSS-hidden — the rest of this component's JSX never mounts.
  if (userMode === 'demo') {
    return <Navigate to="/portfolio" replace />;
  }

  const displayName = userName || user?.name || userEmail?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <PageShell className="mt-6 pb-10">
        <div className="max-w-xl space-y-4">
        {/* Avatar + name */}
        <div className="flex flex-col items-center py-6">
          <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center mb-3">
            <span className="text-2xl font-bold text-white">{initials}</span>
          </div>
          <h1 className="card-title dark:text-gray-100">{displayName}</h1>
          <p className="text-sm text-muted dark:text-gray-400">{userEmail}</p>
        </div>

        {/* Account details */}
        <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          <Row label="Name" value={displayName} />
          <Row label="Email" value={userEmail ?? '—'} />
          <Row label="Company" value={user?.company ?? '—'} />
        </div>

        {/* Sign out */}
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-800 border border-crit-text text-crit-text font-semibold py-3.5 rounded-[8px] text-sm hover:bg-crit-bg dark:hover:bg-red-900/20 transition-colors min-h-[52px] shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign out
        </button>
        </div>
      
    </PageShell>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between px-4 py-3.5 min-h-[52px]">
    <span className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide">{label}</span>
    <span className="text-sm text-text dark:text-gray-100 text-right max-w-[60%] truncate">{value}</span>
  </div>
);

export default Account;
